import React from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../i18n/LanguageContext';
import { ouvrirReglages } from '../lib/consentement';

export default function Footer() {
  const currentYear = new Date().getFullYear();
  const { t } = useLang();

  return (
    <footer className="relative bg-gradient-to-br from-[#33403B] via-[#17453E] to-[#22403A] border-t-4 border-[#4AA294] text-[#F7F4EC]">
      {/* Gradient overlay */}
      <div className="absolute top-0 left-0 right-0 h-[120px] bg-gradient-to-b from-[#4AA294]/10 to-transparent pointer-events-none"></div>

      <div className="relative z-10 max-w-[1400px] mx-auto px-10 pt-15 pb-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">

        {/* Section 1 - About */}
        <div className="flex flex-col gap-4">
          <h3 className="flex items-center gap-2 m-0 text-lg font-black uppercase tracking-wide text-[#EDB82F]">
            <span className="text-2xl animate-bounce">🌳</span>
            MIRUM GAMES
          </h3>
          <p className="text-[13px] leading-relaxed text-[#E8DDD2] m-0 opacity-95">
            {t('footer.tagline')}
          </p>
          <div className="flex gap-2 flex-wrap">
            {[t('footer.badge.interactive'), t('footer.badge.festive'), t('footer.badge.family')].map((badge, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white px-3 py-1.5 rounded-2xl text-[11px] font-semibold uppercase tracking-wide transition-all duration-300 hover:from-[#EDB82F] hover:to-[#4AA294] hover:-translate-y-0.5 hover:shadow-glow"
              >
                {badge}
              </span>
            ))}
          </div>
        </div>

        {/* Section 2 - Links */}
        <div className="flex flex-col gap-4">
          <h4 className="m-0 text-[13px] font-bold uppercase tracking-wide text-[#4AA294]">{t('footer.nav')}</h4>
          <ul className="list-none m-0 p-0 flex flex-col gap-3">
            {[
              { to: '/', icon: '🏠', label: t('nav.home') },
              { to: '/shop', icon: '🛍️', label: t('nav.shop') },
              { to: '/regles-de-jeu', icon: '📖', label: t('nav.rules') },
              { to: '/about', icon: 'ℹ️', label: t('nav.about') },
              { to: '/contact', icon: '❓', label: t('nav.contact') },
            ].map(link => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="relative inline-flex items-center gap-1.5 text-[#7FC9BC] no-underline text-[13px] font-medium transition-all duration-300 hover:text-[#EDB82F] hover:translate-x-1 after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-px after:bg-[#EDB82F] after:transition-all after:duration-300 hover:after:w-full"
                >
                  {link.icon} {link.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>

        {/* Section 3 - Legal */}
        <div className="flex flex-col gap-4">
          <h4 className="m-0 text-[13px] font-bold uppercase tracking-wide text-[#4AA294]">{t('footer.legal')}</h4>
          <ul className="list-none m-0 p-0 flex flex-col gap-3">
            {[
              { to: '/privacy', icon: '🔒', label: t('footer.privacy') },
              { to: '/terms', icon: '📋', label: t('footer.terms') },
              { to: '/payment-security', icon: '💳', label: t('footer.paymentSecurity') },
              { to: '/legal', icon: '⚖️', label: t('footer.legalNotice') },
              { to: '/ma-facture', icon: '🧾', label: t('footer.maFacture') },
            ].map(link => (
              <li key={link.to}>
                <Link
                  to={link.to}
                  className="relative inline-flex items-center gap-1.5 text-[#7FC9BC] no-underline text-[13px] font-medium transition-all duration-300 hover:text-[#EDB82F] hover:translate-x-1 after:absolute after:bottom-[-2px] after:left-0 after:w-0 after:h-px after:bg-[#EDB82F] after:transition-all after:duration-300 hover:after:w-full"
                >
                  {link.icon} {link.label}
                </Link>
              </li>
            ))}
            {/* Sans ce bouton, un choix fait une fois était définitif : le
                bandeau ne réapparaissait plus jamais. */}
            <li>
              <button
                type="button"
                onClick={ouvrirReglages}
                data-cta="pied:gerer-cookies"
                className="relative inline-flex items-center gap-1.5 text-[#7FC9BC] bg-transparent border-none p-0 cursor-pointer text-[13px] font-medium transition-all duration-300 hover:text-[#EDB82F] hover:translate-x-1"
              >
                🍪 {t('cookies.gerer')}
              </button>
            </li>
          </ul>
        </div>

        {/* Section 4 - Social & Newsletter */}
        <div className="flex flex-col gap-4">
          <h4 className="m-0 text-[13px] font-bold uppercase tracking-wide text-[#4AA294]">{t('footer.connected')}</h4>
          <div className="flex gap-3 flex-wrap md:justify-start">
            <a
              href="https://www.instagram.com/mirumgames/"
              title="Instagram"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-11 h-11 bg-gradient-to-br from-[#4AA294] to-[#2F7E72] border-2 border-[#EDB82F] rounded-lg transition-all duration-300 hover:from-[#EDB82F] hover:to-[#4AA294] hover:-translate-y-1 hover:scale-110 hover:shadow-glow"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.057-1.645.069-4.849.069-3.205 0-3.584-.012-4.849-.069-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.322a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z" />
              </svg>
            </a>
            <a
              href="https://www.facebook.com/people/MirumGames/61553240464727/?locale=fr_FR"
              title="Facebook"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center w-11 h-11 bg-gradient-to-br from-[#4AA294] to-[#2F7E72] border-2 border-[#EDB82F] rounded-lg transition-all duration-300 hover:from-[#EDB82F] hover:to-[#4AA294] hover:-translate-y-1 hover:scale-110 hover:shadow-glow"
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
              </svg>
            </a>
          </div>
          <p className="text-xs font-bold uppercase tracking-wide text-[#4AA294] m-0">{t('footer.newsletter')}</p>
          <form className="flex gap-2 border-2 border-[#4AA294] rounded-sm p-1 bg-[#4AA294]/10 transition-all duration-300 focus-within:border-[#EDB82F] focus-within:shadow-glow">
            <input
              type="email"
              placeholder={t('footer.newsletterPlaceholder')}
              className="flex-1 bg-transparent border-none text-[#F7F4EC] px-3 py-2.5 text-xs outline-none placeholder:text-[#4AA294]/60"
              required
            />
            <button
              type="submit"
              className="bg-gradient-to-br from-[#4AA294] to-[#2F7E72] text-white text-base px-3 py-0 rounded-sm transition-all duration-300 hover:from-[#EDB82F] hover:to-[#4AA294] hover:scale-110"
            >
              📬
            </button>
          </form>
        </div>
      </div>

      {/* Divider */}
      <div className="max-w-[1400px] mx-auto mt-10 h-0.5 bg-gradient-to-r from-transparent via-[#4AA294] to-transparent"></div>

      {/* Bottom */}
      <div className="relative z-10 max-w-[1400px] mx-auto px-10 py-6 flex flex-wrap justify-between items-center gap-5">
        <p className="m-0 text-xs text-[#B0B0B0] font-medium text-center md:text-left w-full md:w-auto">
          © {currentYear} <span className="text-[#4AA294] font-bold text-[13px]">Mirum Games</span>.
          {' '}{t('footer.madeWith')} <span className="inline-block animate-pulse text-[#F4A6C1] text-sm">💚</span> {t('footer.madeFor')}
        </p>
        <div className="flex gap-4 text-base justify-center w-full md:w-auto">
          <span className="inline-block animate-bounce">🎮</span>
          <span className="inline-block animate-bounce" style={{ animationDelay: '0.3s' }}>🧩</span>
          <span className="inline-block animate-bounce" style={{ animationDelay: '0.6s' }}>✨</span>
        </div>
      </div>
    </footer>
  );
}
